/**
 * /advertisers/invoices — self-serve advertiser invoices page (feature #150).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `AdvertiserInvoicesPage`. The flat-file naming convention uses dots as path
 * separators, so `advertisers.invoices.tsx` maps to `/advertisers/invoices`.
 *
 * `validateSearch` parses the optional `?advertiserId=` and `?year=` query
 * params so deep-links from the advertiser-portal page (or ops Slack) land on
 * the same filtered view. The values are still read by the page from its
 * own state; the search schema just keeps the URL clean if callers supply
 * junk values.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AdvertiserInvoicesPage } from "@/features/advertiser-invoices/AdvertiserInvoicesPage";

interface AdvertiserInvoicesSearch {
	advertiserId?: string;
	year?: number;
}

export const advertisersInvoicesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/advertisers/invoices",
	validateSearch: (raw: Record<string, unknown>): AdvertiserInvoicesSearch => {
		const out: AdvertiserInvoicesSearch = {};
		if (typeof raw.advertiserId === "string" && raw.advertiserId.trim().length > 0) {
			out.advertiserId = raw.advertiserId.trim();
		}
		const yearRaw = raw.year;
		if (typeof yearRaw === "number" && Number.isInteger(yearRaw)) {
			if (yearRaw >= 1970 && yearRaw <= 9999) out.year = yearRaw;
		} else if (typeof yearRaw === "string" && yearRaw.length > 0) {
			const n = Number(yearRaw);
			if (Number.isInteger(n) && n >= 1970 && n <= 9999) out.year = n;
		}
		return out;
	},
	component: AdvertiserInvoicesPage,
});

export default advertisersInvoicesRoute;

// Re-export the page for the root wire-up so a single import path can reach
// both the route and the component.
export { AdvertiserInvoicesPage } from "@/features/advertiser-invoices/AdvertiserInvoicesPage";
